import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Link2, Send, FileText, UserPlus, Truck } from 'lucide-react';
import { toast } from 'sonner';

// Validation functions
const validateCPF = (cpf) => {
  cpf = cpf.replace(/[^\d]/g, '');
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  let sum = 0, rest;
  for (let i = 1; i <= 9; i++) sum += parseInt(cpf.substring(i-1, i)) * (11 - i);
  rest = (sum * 10) % 11;
  if (rest === 10 || rest === 11) rest = 0;
  if (rest !== parseInt(cpf.substring(9, 10))) return false;
  sum = 0;
  for (let i = 1; i <= 10; i++) sum += parseInt(cpf.substring(i-1, i)) * (12 - i);
  rest = (sum * 10) % 11;
  if (rest === 10 || rest === 11) rest = 0;
  return rest === parseInt(cpf.substring(10, 11));
};

const validateCNPJ = (cnpj) => {
  cnpj = cnpj.replace(/[^\d]/g, '');
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
  let size = cnpj.length - 2, numbers = cnpj.substring(0, size), digits = cnpj.substring(size), sum = 0, pos = size - 7;
  for (let i = size; i >= 1; i--) { sum += numbers.charAt(size - i) * pos--; if (pos < 2) pos = 9; }
  let result = sum % 11 < 2 ? 0 : 11 - sum % 11;
  if (result !== parseInt(digits.charAt(0))) return false;
  size++; numbers = cnpj.substring(0, size); sum = 0; pos = size - 7;
  for (let i = size; i >= 1; i--) { sum += numbers.charAt(size - i) * pos--; if (pos < 2) pos = 9; }
  result = sum % 11 < 2 ? 0 : 11 - sum % 11;
  return result === parseInt(digits.charAt(1));
};

// Format functions
const formatCNPJ = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5').slice(0, 18);
};

const formatCPF = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4').slice(0, 14);
};

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

export default function ClientesPage() {
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [documentType, setDocumentType] = useState('cnpj'); // cnpj or cpf
  const [formData, setFormData] = useState({
    nome: '',
    cnpj_cpf: '',
    tipo: 'hospital',
    endereco: '',
    responsavel_tecnico: '',
    telefone: '',
    email: '',
  });
  
  // Portal do Cliente - Criar usuário
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [selectedClienteForUser, setSelectedClienteForUser] = useState(null);
  const [userForm, setUserForm] = useState({
    nome: '',
    email: '',
    telefone: '',
    whatsapp: '',
    senha: '',
  });

  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data);
      setFilteredClientes(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filtered = clientes.filter(cliente =>
      cliente.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cliente.codigo && cliente.codigo.toLowerCase().includes(searchTerm.toLowerCase())) ||
      cliente.cnpj_cpf.includes(searchTerm)
    );
    setFilteredClientes(filtered);
  }, [searchTerm, clientes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate CNPJ/CPF (opcional)
    const doc = (formData.cnpj_cpf || '').replace(/[^\d]/g, '');
    if (doc.length === 11) {
      if (!validateCPF(formData.cnpj_cpf)) {
        toast.error('CPF inválido!');
        return;
      }
    } else if (doc.length === 14) {
      if (!validateCNPJ(formData.cnpj_cpf)) {
        toast.error('CNPJ inválido!');
        return;
      }
    } else if (doc.length > 0) {
      toast.error('CPF/CNPJ incompleto. Complete ou deixe em branco.');
      return;
    }
    if ((formData.telefone || '').replace(/\D/g, '').length < 10) {
      toast.error('Informe o telefone com DDD');
      return;
    }
    
    try {
      if (editingCliente) {
        await api.put(`/clientes/${editingCliente.id}`, formData);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await api.post('/clientes', formData);
        toast.success('Cliente criado com sucesso!');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchClientes();
    } catch (error) {
      toast.error('Erro ao salvar cliente');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este cliente?')) return;
    try {
      await api.delete(`/clientes/${id}`);
      toast.success('Cliente excluído com sucesso!');
      fetchClientes();
    } catch (error) {
      toast.error('Erro ao excluir cliente');
    }
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    const doc = cliente.cnpj_cpf.replace(/[^\d]/g, '');
    setDocumentType(doc.length <= 11 ? 'cpf' : 'cnpj');
    setFormData({
      nome: cliente.nome,
      cnpj_cpf: cliente.cnpj_cpf,
      tipo: cliente.tipo,
      endereco: cliente.endereco,
      responsavel_tecnico: cliente.responsavel_tecnico,
      telefone: cliente.telefone,
      email: cliente.email,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCliente(null);
    setDocumentType('cnpj');
    setFormData({
      nome: '',
      cnpj_cpf: '',
      tipo: 'hospital',
      endereco: '',
      responsavel_tecnico: '',
      telefone: '',
      email: '',
    });
  };

  // Criar usuário para o portal do cliente
  const handleCreatePortalUser = async () => {
    if (!userForm.nome || !userForm.email || !userForm.senha) {
      toast.error('Preencha nome, email e senha');
      return;
    }
    
    try {
      await api.post('/portal-cliente/usuarios', {
        cliente_id: selectedClienteForUser.id,
        nome: userForm.nome,
        email: userForm.email,
        telefone: userForm.telefone,
        whatsapp: userForm.whatsapp || userForm.telefone,
        senha: userForm.senha,
        receber_notificacoes_email: true,
        receber_notificacoes_whatsapp: true,
      });
      
      toast.success('Usuário do portal criado com sucesso!');
      setShowUserDialog(false);
      setUserForm({ nome: '', email: '', telefone: '', whatsapp: '', senha: '' });
      setSelectedClienteForUser(null);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao criar usuário';
      toast.error(msg);
    }
  };

  const openCreateUserDialog = (cliente) => {
    setSelectedClienteForUser(cliente);
    setUserForm({
      nome: cliente.responsavel_tecnico || '',
      email: cliente.email || '',
      telefone: cliente.telefone || '',
      whatsapp: cliente.telefone || '',
      senha: '',
    });
    setShowUserDialog(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  return (
    <div data-testid="clientes-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Clientes
          </h1>
          <p className="text-lg text-slate-600 mt-2">Gerenciar clínicas e hospitais</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="btn-add-cliente"
              onClick={() => resetForm()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingCliente ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="nome">Nome da Clínica/Hospital *</Label>
                  <Input
                    data-testid="input-nome"
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="cnpj_cpf">CNPJ/CPF <span className="text-xs text-slate-400">(opcional)</span></Label>
                  <div className="flex gap-2 mb-2">
                    <button type="button" onClick={() => { setDocumentType('cnpj'); setFormData({...formData, cnpj_cpf: ''}); }} className={`px-3 py-1 text-xs rounded ${documentType === 'cnpj' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>CNPJ</button>
                    <button type="button" onClick={() => { setDocumentType('cpf'); setFormData({...formData, cnpj_cpf: ''}); }} className={`px-3 py-1 text-xs rounded ${documentType === 'cpf' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>CPF</button>
                  </div>
                  <Input
                    data-testid="input-cnpj"
                    id="cnpj_cpf"
                    value={formData.cnpj_cpf}
                    onChange={(e) => setFormData({ ...formData, cnpj_cpf: documentType === 'cnpj' ? formatCNPJ(e.target.value) : formatCPF(e.target.value) })}
                    placeholder={documentType === 'cnpj' ? '00.000.000/0000-00' : '000.000.000-00'}
                  />
                </div>
                <div>
                  <Label htmlFor="tipo">Tipo</Label>
                  <select
                    data-testid="select-tipo"
                    id="tipo"
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value="hospital">Hospital</option>
                    <option value="clinica_medica">Clínica Médica</option>
                    <option value="odontologica">Odontológica</option>
                    <option value="estetica">Estética</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="endereco">Endereço <span className="text-xs text-slate-400">(opcional)</span></Label>
                  <Input
                    data-testid="input-endereco"
                    id="endereco"
                    value={formData.endereco}
                    onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="responsavel_tecnico">Responsável Técnico <span className="text-xs text-slate-400">(opcional)</span></Label>
                  <Input
                    data-testid="input-responsavel"
                    id="responsavel_tecnico"
                    value={formData.responsavel_tecnico}
                    onChange={(e) => setFormData({ ...formData, responsavel_tecnico: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="telefone">Telefone *</Label>
                  <Input
                    data-testid="input-telefone"
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: formatPhone(e.target.value) })}
                    placeholder="(00) 00000-0000"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email <span className="text-xs text-slate-400">(opcional)</span></Label>
                  <Input
                    data-testid="input-email"
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button data-testid="btn-submit" type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingCliente ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="max-w-md">
          <Label htmlFor="search">Buscar clientes</Label>
          <Input
            id="search"
            placeholder="Buscar por nome, código ou CNPJ/CPF..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mt-1"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">CNPJ/CPF</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Tipo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Telefone</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredClientes.map((cliente) => (
              <tr key={cliente.id} data-testid={`cliente-row-${cliente.id}`} className="hover:bg-slate-50">
                <td className="px-6 py-4 text-sm text-slate-900">{cliente.nome}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{cliente.cnpj_cpf}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{cliente.tipo}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{cliente.telefone}</td>
                <td className="px-6 py-4">
                  <StatusBadge status={cliente.status} />
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex gap-2">
                    <Button
                      data-testid={`btn-edit-${cliente.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(cliente)}
                      title="Editar cliente"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-tracking-${cliente.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const url = `${window.location.origin}/rastrear/${cliente.id}`;
                        navigator.clipboard.writeText(url);
                        toast.success('Link de rastreamento copiado!');
                      }}
                      title="Copiar link de rastreamento"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Link2 className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-whatsapp-tracking-${cliente.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const url = `${window.location.origin}/rastrear/${cliente.id}`;
                        const telefone = cliente.telefone?.replace(/\D/g, '') || '';
                        const msg = `Olá ${cliente.nome}! Acompanhe seus materiais: ${url}`;
                        window.open(`https://wa.me/55${telefone}?text=${encodeURIComponent(msg)}`, '_blank');
                      }}
                      title="Enviar link por WhatsApp"
                      className="text-green-600 hover:text-green-700"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-pdf-${cliente.id}`}
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          toast.info('Gerando relatório PDF...');
                          const response = await api.get(`/clientes/${cliente.id}/historico-pdf`, {
                            responseType: 'blob'
                          });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `historico-${cliente.nome.replace(/\s+/g, '_')}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success('Relatório PDF baixado com sucesso!');
                        } catch (error) {
                          toast.error('Erro ao gerar PDF');
                        }
                      }}
                      title="Baixar histórico em PDF"
                      className="text-purple-600 hover:text-purple-700"
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-delete-${cliente.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(cliente.id)}
                      className="text-red-600 hover:text-red-700"
                      title="Excluir cliente"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openCreateUserDialog(cliente)}
                      className="text-amber-600 hover:text-amber-700 border-amber-300"
                      title="Criar acesso ao Portal do Cliente"
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredClientes.length === 0 && searchTerm && (
          <div className="text-center py-12 text-slate-500">
            Nenhum cliente encontrado para &quot;{searchTerm}&quot;
          </div>
        )}
        {clientes.length === 0 && !searchTerm && (
          <div className="text-center py-12 text-slate-500">
            Nenhum cliente cadastrado
          </div>
        )}
      </div>

      {/* Dialog Criar Usuário Portal */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Criar Acesso ao Portal
            </DialogTitle>
          </DialogHeader>
          {selectedClienteForUser && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <p className="font-medium text-blue-800">Cliente: {selectedClienteForUser.nome}</p>
                <p className="text-blue-600 text-xs mt-1">
                  O usuário poderá acessar o portal em: <code>/portal</code>
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nome do Usuário *</Label>
                  <Input
                    value={userForm.nome}
                    onChange={(e) => setUserForm(f => ({...f, nome: e.target.value}))}
                    placeholder="Nome completo"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm(f => ({...f, email: e.target.value}))}
                    placeholder="email@cliente.com"
                  />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input
                    value={userForm.telefone}
                    onChange={(e) => setUserForm(f => ({...f, telefone: formatPhone(e.target.value)}))}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div>
                  <Label>WhatsApp</Label>
                  <Input
                    value={userForm.whatsapp}
                    onChange={(e) => setUserForm(f => ({...f, whatsapp: formatPhone(e.target.value)}))}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Senha *</Label>
                  <Input
                    type="password"
                    value={userForm.senha}
                    onChange={(e) => setUserForm(f => ({...f, senha: e.target.value}))}
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowUserDialog(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreatePortalUser}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Criar Acesso
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}